import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useExtensionDetection } from '../hooks/useExtensionDetection';
import { getExtensionDownloadUrl, BrowserType } from '../utils/browserDetection';
import './ExtensionBanner.css';

export const ExtensionBanner: React.FC = () => {
  const location = useLocation();
  const { isDetected, browser, isLoading } = useExtensionDetection();
  const [isDismissed, setIsDismissed] = useState(false);
  
  // Hide banner on login/landing page
  const isLoginPage = location.pathname === '/' || location.pathname === '/login';

  // Check localStorage for dismissal on mount
  useEffect(() => {
    const dismissed = localStorage.getItem('kairos-extension-banner-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('kairos-extension-banner-dismissed', 'true');
  };

  const downloadUrl = getExtensionDownloadUrl(browser);

  // Don't show if: on login page, detected, loading, dismissed, or no download URL
  const shouldShow = !(isLoginPage || isDetected || isLoading || isDismissed || downloadUrl === undefined);
  
  if (!shouldShow) {
    return null;
  }

  const getBrowserName = (browserType: BrowserType): string => {
    switch (browserType) {
      case 'chrome':
        return 'Chrome';
      case 'edge':
        return 'Edge';
      case 'firefox':
        return 'Firefox';
      case 'brave':
        return 'Brave';
      case 'opera':
        return 'Opera';
      default:
        return 'Browser';
    }
  };

  return (
    <div className="extension-banner">
      <div className="extension-banner-content">
        <div className="extension-banner-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2L2 7L12 12L22 7L12 2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 17L12 22L22 17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 12L12 17L22 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="extension-banner-text">
          <div className="extension-banner-title">
            Install the Kairos Browser Extension
          </div>
          <div className="extension-banner-description">
            <strong>Why you need it:</strong> Get instant job analysis and one-click application tracking directly from job boards like LinkedIn, Indeed, and more. Save time by avoiding manual copy-paste of job descriptions.
          </div>
        </div>
        <div className="extension-banner-actions">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="extension-banner-button"
          >
            Install for {getBrowserName(browser)}
          </a>
          <button
            onClick={handleDismiss}
            className="extension-banner-dismiss"
            aria-label="Dismiss banner"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

